import React from 'react'
import ReactMarkdown from 'react-markdown'
import clsx from 'clsx'
import rehypeRaw from 'rehype-raw'
import { useIntersectionObserver } from 'usehooks-ts'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_COLOR_NEUTRAL, BASIC_BUTTON_TYPE_FLAT } from '@divvy-web/skylab.basicbutton'
import Takeover, { TakeoverActionBar, TakeoverBody, TakeoverContent } from '@divvy-web/skylab.takeover'
import Tooltip, { TOOLTIP_POSITION_TOP } from '@divvy-web/skylab.tooltip'

import styles from './styles.module.scss'

interface AgreementTakeoverProps {
  isAcceptingTilaAgreement: boolean
  isShowing: boolean
  hasAgreedToTila: boolean
  tilaContent: string
  onCancel: () => void
  onAgreeClick: () => void
  isLoggedInUserAuthorizedSigner: boolean
}

const AgreementTakeover = ({
  isAcceptingTilaAgreement,
  isShowing,
  hasAgreedToTila,
  tilaContent,
  isLoggedInUserAuthorizedSigner,
  onCancel,
  onAgreeClick,
}: AgreementTakeoverProps) => {
  const [getClassName, makeTestId] = useNameFormatter('AgreementTakeover')
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 1,
  })

  return (
    <Takeover
      className={clsx(getClassName(''), 'dd-privacy-allow')}
      dataTestId={makeTestId('')}
      isShowing={isShowing}
      onCloseComplete={onCancel}
    >
      <TakeoverContent>
        <TakeoverBody>
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{tilaContent}</ReactMarkdown>
          <span
            ref={ref}
            className={styles[getClassName('ref-container')]}
            data-testid='ref-container'
          />
        </TakeoverBody>
        <TakeoverActionBar className={styles[getClassName('action-bar')]}>
          {!hasAgreedToTila && (
            <Tooltip
              dataTestId={makeTestId('scroll-to-bottom-tooltip')}
              disableTooltip={(isIntersecting && isLoggedInUserAuthorizedSigner) || false}
              html={
                isLoggedInUserAuthorizedSigner ? (
                  <FormattedMessage
                    defaultMessage='Scroll to the bottom of the terms document to enable the agree button.'
                    id='sputnik.AgreementTakeover__7zcKKH'
                  />
                ) : (
                  <FormattedMessage
                    defaultMessage='Only the authorized signer can agree.'
                    id='sputnik.AgreementTakeover__GRWP7K'
                  />
                )
              }
              position={TOOLTIP_POSITION_TOP}
            >
              <BasicButton
                className={styles[getClassName('top-footer-button')]}
                dataTestId={makeTestId('review-offer')}
                disabled={isAcceptingTilaAgreement || !isIntersecting || !isLoggedInUserAuthorizedSigner}
                onClick={onAgreeClick}
              >
                <FormattedMessage
                  defaultMessage='Agree'
                  id='sputnik.AgreementTakeover__ISly67'
                />
              </BasicButton>
            </Tooltip>
          )}
          <BasicButton
            className={styles[getClassName('bottom-footer-button')]}
            color={BASIC_BUTTON_COLOR_NEUTRAL}
            dataTestId={makeTestId('dismiss')}
            type={BASIC_BUTTON_TYPE_FLAT}
            onClick={onCancel}
          >
            <FormattedMessage
              defaultMessage='Cancel'
              id='sputnik.AgreementTakeover__47FYwb'
            />
          </BasicButton>
        </TakeoverActionBar>
      </TakeoverContent>
    </Takeover>
  )
}

export default AgreementTakeover
